import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath } from 'react-router-dom';
import type { DialogProps } from '@mui/material';
import type { AxiosError, AxiosResponse } from 'axios';

import settings from 'src/settings';
import HttpServiceInstance from 'src/services/api/http';
import ErrorDialog from 'src/components/common/ErrorHandler/ErrorDialog';

/**
 * Helper function to format a string, an array of strings or an object to a single text-string.
 */
export const responseDataToText = (
  data: string | string[] | Record<string, string> | undefined,
): string | undefined => {
  if (typeof data === 'string' && !!data.length) {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map((textElement) => textElement).join('\n');
  }

  if (typeof data === 'object') {
    if (Object.hasOwn(data, 'detail')) {
      return data.detail;
    }

    return Object.entries(data)
      .map(
        ([entryName, entryText]) =>
          `${entryName.replace('_', ' ')}: ${entryText}`,
      )
      .join('\n');
  }
};

/**
 * Determines whether the specified request URL matches for any element of the endpoint paths.
 */
export const matchEndpoints = (requestURL: string, endpointPaths: string[]) => {
  const pathname = requestURL.replace(settings.VITE_APP_API_URL, '');

  return endpointPaths.some((endpointPath) =>
    matchPath(pathname, {
      path: endpointPath,
    }),
  );
};

/**
 * Severity levels for individual status codes.
 */
export interface SeverityLevels {
  /** Status codes that are not displayed at all */
  none: number[];
  /** Status codes that are displayed as regular notice. */
  notice: number[];
  /** Status codes that are not listed in `none` or `notice` are displayed as an error. */
}

export const defaultSeverityLevels: SeverityLevels = {
  none: [],
  notice: [],
};

export interface Status {
  statusCode: number;
  statusMessage: string | undefined;
  statusText: string;
}

export interface ErrorHandlerProps extends Omit<DialogProps, 'open'> {
  errorResponse?: AxiosError | null;
  ignoredEndpoints?: string[];
  intercept?: boolean;
  severityLevels?: Partial<SeverityLevels>;
}

const ErrorHandler: FC<ErrorHandlerProps> = ({
  errorResponse,
  ignoredEndpoints,
  intercept = false,
  severityLevels: _severityLevels = defaultSeverityLevels,
}) => {
  const { t } = useTranslation('common');
  const [errorMessage, setErrorMessage] = useState('');
  const [severity, setSeverity] = useState<'notice' | 'error' | ''>('');
  const [status, setStatus] = useState<Status>();

  const severityLevels = {
    none: _severityLevels.none || defaultSeverityLevels.none,
    notice: _severityLevels.notice || defaultSeverityLevels.notice,
  };

  /**
   * Differentiate between severity levels depending on the status code.
   */
  const getSeverity = (_status: Status) =>
    severityLevels.notice.includes(_status.statusCode) ? 'notice' : 'error';

  /**
   * Differentiate between status codes to generate the desired error message.
   */
  const getErrorMessage = (_status: Status): string => {
    const isClientError = _status.statusCode >= 400 && _status.statusCode < 500;
    let errorMessage = t('somethingWentWrong');

    // Only replace generic error message with status message for client errors.
    // But also for our hand tailored network error status, which we gave the status code `0`.
    if (isClientError || _status.statusCode === 0) {
      errorMessage = _status.statusMessage || errorMessage;
    }

    return errorMessage;
  };

  /**
   * Differentiate between axios responses and axios errors and check wether a dialog should be shown for
   * the given status code and request endpoint before actually setting the relevant states.
   */
  const handleStatus = (responseOrError: AxiosError | AxiosResponse) => {
    // ignore requests that are aborted in useEffect cleanup
    if ('code' in responseOrError && responseOrError.code === 'ERR_CANCELED') {
      return;
    }

    const response =
      'isAxiosError' in responseOrError
        ? responseOrError.response
        : responseOrError;

    const newStatus = response
      ? // Use provided response to set status state.
        {
          statusCode: response.status,
          statusMessage: responseDataToText(response.data),
          statusText: response.statusText,
        }
      : // Create generic network error state, when no response could be received.
        {
          statusCode: 0,
          statusMessage: t('networkError'),
          statusText: 'Network Error',
        };

    if (severityLevels.none.includes(newStatus.statusCode)) {
      return;
    }

    const url = responseOrError?.config?.url;
    if (ignoredEndpoints && (!url || matchEndpoints(url, ignoredEndpoints))) {
      return;
    }

    setErrorMessage(getErrorMessage(newStatus));
    setSeverity(getSeverity(newStatus));
    setStatus(newStatus);
  };

  // Handle error that was explicitly passed to this component.
  useEffect(() => {
    if (errorResponse) handleStatus(errorResponse);
  }, [errorResponse]);

  // Intercept all requests and response on mount if `intercept` was set to `true`.
  useEffect(() => {
    if (!intercept) return;

    const requestInterceptor =
      HttpServiceInstance.service.interceptors.request.use(
        (config) => config,
        (error) => {
          handleStatus(error);
          return Promise.reject(error);
        },
      );

    const responseInterceptor =
      HttpServiceInstance.service.interceptors.response.use(
        (response) => response,
        (error) => {
          handleStatus(error);
          return Promise.reject(error);
        },
      );

    return () => {
      HttpServiceInstance.service.interceptors.request.eject(
        requestInterceptor,
      );

      HttpServiceInstance.service.interceptors.response.eject(
        responseInterceptor,
      );
    };
  }, []);

  return (
    <ErrorDialog
      errorMessage={errorMessage}
      severity={severity}
      isOpen={!!status}
      onClose={() => setStatus(undefined)}
    />
  );
};

export default ErrorHandler;
